/**
 * AgentLibrary — the unified account agent library.
 *
 * Merges the former "My Agents" (workspace personas) and "Templates" (seeded
 * system personas) tabs into ONE Processes-style list (mirrors
 * AgentProcessLibrary in AgentProcessesView.tsx):
 *   - source filter chips (All / My Agents / Templates), keyed on `source`,
 *   - a Group filter dropdown that doubles as inline group management
 *     (filter by group + add a new group + delete a group),
 *   - a client-side substring search box, and
 *   - client-side pagination.
 *
 * Editing follows the Processes pattern — standalone routes, not a side panel:
 * a workspace agent opens the full-page editor at /agents/:personaId, "+ New
 * Agent" opens /agents/new. System templates are read-only; their only action
 * is "Copy to My Agents", which lands on the new editable agent's route
 * (mirrors the Processes "Copy to My Library" affordance — the agent editor
 * loads workspace personas by id, not system ones, so there is no template
 * editor route by design).
 *
 * This is a block component (no full-height chrome) rendered inside the
 * AgentsView "Agents" tab body, whose surrounding ListDetailLayout owns the
 * scroll container — same placement contract as AgentProcessLibrary.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { Input } from '@localagent/ui/components/input';
import { DataList } from '@localagent/ui/components/data-list';
import { useFilteredList, type FilterValue } from '@localagent/ui/hooks/use-filtered-list';
import { usePagination } from '@localagent/ui/hooks/use-pagination';
import { cn } from '@localagent/ui/lib/utils';
import { toast } from '@localagent/ui/components/sonner';
import {
  useAgentDefinitions,
  useSystemAgents,
  useAgentGroups,
  useCreateAgentGroup,
  useDeleteAgentGroup,
  useDeleteAgentDefinition,
  useDuplicateAgentDefinition,
  useCopySystemAgent,
} from '../hooks/useAgentDefinitions';
import type {
  PersonaWithConfig,
  SystemPersonaWithConfig,
  PersonaAgentConfig as AgentConfig,
} from '@localagent/sdk';

// A workspace persona and a normalized system persona share one card shape,
// discriminated by `source` — the chip filter keys on this exactly like the
// Processes library keys on `is_system`.
type UnifiedPersona = PersonaWithConfig & { source: 'workspace' | 'system' };

// Source scope: '' = all, 'mine' = workspace customs, 'templates' = seeded
// system personas. It defaults to 'mine' so the seeded template corpus never
// buries the user's own agents on page 1.
type SourceScope = '' | 'mine' | 'templates';

function personaInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}

// Normalize a system persona (a { persona, config } pair with a `category`
// instead of a group) into the workspace card shape, tagged source:'system'.
// Copied from AgentsView so the merged list owns its own assembly.
function systemPersonaToCard(sp: SystemPersonaWithConfig): UnifiedPersona {
  let agent_config: AgentConfig | undefined;
  if (sp.config?.agent_config_json) {
    try {
      agent_config = JSON.parse(sp.config.agent_config_json) as AgentConfig;
    } catch { /* ignore parse errors — card still renders without config */ }
  }
  return {
    id: sp.persona.id,
    name: sp.persona.name,
    description: sp.persona.description,
    emoji: sp.persona.emoji,
    group_id: null,
    group_name: sp.persona.category,
    organization_id: '',
    account_id: '',
    is_default: false,
    created_at: sp.persona.created_at ?? '',
    updated_at: sp.persona.updated_at ?? '',
    latest_config_id: sp.config?.id ?? null,
    agent_config,
    source: 'system',
  };
}

const searchFn = (item: UnifiedPersona, query: string): boolean => {
  const q = query.toLowerCase();
  return (
    item.name.toLowerCase().includes(q) ||
    (item.description?.toLowerCase().includes(q) ?? false)
  );
};

const filterFns: Record<string, (item: UnifiedPersona, value: FilterValue) => boolean> = {
  // Group filter keys on group_id — system templates carry no group_id, so
  // selecting a group naturally scopes to workspace agents in that group.
  group: (item, value) => {
    if (!value || Array.isArray(value)) return true;
    return item.group_id === value;
  },
};

const SOURCE_CHIPS: Array<{ value: SourceScope; label: string; testid: string }> = [
  { value: '', label: 'All', testid: 'agent-filter-all' },
  { value: 'mine', label: 'My Agents', testid: 'agent-filter-mine' },
  { value: 'templates', label: 'Templates', testid: 'agent-filter-templates' },
];

// ---------------------------------------------------------------------------
// GroupFilterDropdown — a hand-rolled combobox (no Popover primitive exists in
// @localagent/ui; this mirrors DataList's internal MultiSelectFilter approach).
// It is both a filter (pick a group to scope the list) and lightweight group
// management: add a group inline, or delete one (its agents are ungrouped
// first). Group rename and the old ungroup/move/delete-agents modal are
// intentionally dropped in the round-3 UX.
// ---------------------------------------------------------------------------
function GroupFilterDropdown({
  groups,
  selectedGroupId,
  onSelect,
  onCreate,
  onDelete,
  isBusy,
}: {
  groups: Array<{ id: string; name: string }>;
  selectedGroupId: string;
  onSelect: (groupId: string) => void;
  onCreate: (name: string) => void;
  onDelete: (groupId: string) => void;
  isBusy: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const selectedName = groups.find(g => g.id === selectedGroupId)?.name ?? 'All groups';

  const submitNew = () => {
    const name = newName.trim();
    if (!name) return;
    onCreate(name);
    setNewName('');
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        size="sm"
        data-testid="agent-group-filter"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="h-8"
      >
        Group: {selectedName}
        <span className="ml-1 text-muted-foreground">▾</span>
      </Button>
      {open && (
        <div
          className="absolute z-20 mt-1 w-64 rounded-md border bg-popover p-1 shadow-md"
          data-testid="agent-group-filter-menu"
        >
          <button
            type="button"
            data-testid="agent-group-filter-option-all"
            className={cn(
              'flex w-full items-center rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
              selectedGroupId === '' && 'font-medium text-primary',
            )}
            onClick={() => { onSelect(''); setOpen(false); }}
          >
            All groups
          </button>
          {groups.map(g => (
            <div key={g.id} className="group flex items-center gap-1">
              <button
                type="button"
                data-testid={`agent-group-filter-option-${g.id}`}
                className={cn(
                  'flex min-w-0 flex-1 items-center rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
                  selectedGroupId === g.id && 'font-medium text-primary',
                )}
                onClick={() => { onSelect(g.id); setOpen(false); }}
              >
                <span className="truncate">{g.name}</span>
              </button>
              <Button
                variant="ghost"
                size="sm"
                data-testid={`agent-group-delete-${g.id}`}
                title={`Delete group "${g.name}" (its agents are ungrouped)`}
                className="h-7 w-7 shrink-0 p-0 text-muted-foreground opacity-0 group-hover:opacity-100"
                disabled={isBusy}
                onClick={() => onDelete(g.id)}
              >
                🗑
              </Button>
            </div>
          ))}
          <div className="mt-1 flex items-center gap-1 border-t p-1 pt-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } }}
              placeholder="New group…"
              data-testid="agent-group-add-input"
              className="h-8 flex-1 text-xs"
            />
            <Button
              size="sm"
              data-testid="agent-group-add-submit"
              className="h-8"
              disabled={!newName.trim() || isBusy}
              onClick={submitNew}
            >
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AgentLibrary() {
  const navigate = useNavigate();

  const { data: workspaceRaw, isLoading: workspaceLoading, error: workspaceError } = useAgentDefinitions();
  const { data: systemRaw, isLoading: systemLoading } = useSystemAgents();
  const { data: groups } = useAgentGroups();

  const createGroup = useCreateAgentGroup();
  const deleteGroup = useDeleteAgentGroup();
  const deleteAgent = useDeleteAgentDefinition();
  const duplicateAgent = useDuplicateAgentDefinition();
  const copySystem = useCopySystemAgent();

  const allAgents = useMemo<UnifiedPersona[]>(() => {
    const workspace: UnifiedPersona[] = (workspaceRaw ?? []).map(p => {
      const group = p.group_id ? groups?.find(g => g.id === p.group_id) : undefined;
      return { ...p, group_name: group?.name ?? p.group_name ?? null, source: 'workspace' };
    });
    const system: UnifiedPersona[] = (systemRaw ?? []).map(systemPersonaToCard);
    return [...workspace, ...system];
  }, [workspaceRaw, systemRaw, groups]);

  // Newest-first so a just-created/duplicated/copied agent lands on page 1
  // (system rows seeded long ago sink lower — chips are how users focus, not
  // sort order). Mirrors AgentProcessLibrary's rationale.
  const sortedAgents = useMemo(
    () =>
      [...allAgents].sort((a, b) => {
        const at = a.created_at ? Date.parse(a.created_at) : 0;
        const bt = b.created_at ? Date.parse(b.created_at) : 0;
        return bt - at;
      }),
    [allAgents],
  );

  const [sourceScope, setSourceScope] = useState<SourceScope>('mine');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const scopedAgents = useMemo(
    () =>
      sortedAgents.filter((item) => {
        if (sourceScope === 'mine') return item.source === 'workspace';
        if (sourceScope === 'templates') return item.source === 'system';
        return true;
      }),
    [sortedAgents, sourceScope],
  );

  const { filteredItems, searchValue, onSearchChange, filterValues, onFilterChange } =
    useFilteredList({ items: scopedAgents, searchFn, filterFns });

  const groupScope = (filterValues.group as string | undefined) ?? '';

  const pagination = usePagination({
    totalItems: filteredItems.length,
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
  });

  // Return to page 1 whenever any facet changes.
  useEffect(() => {
    pagination.reset();
  }, [searchValue, sourceScope, groupScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagedItems = filteredItems.slice(pagination.startIndex, pagination.endIndex);

  const groupOptions = useMemo(
    () => (groups ?? []).map(g => ({ id: g.id, name: g.name })),
    [groups],
  );

  const handleCreateGroup = useCallback((name: string) => {
    createGroup.mutate(
      { name },
      {
        onSuccess: (group) => {
          onFilterChange('group', group.id);
          toast.success(`Group "${group.name}" created`);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  }, [createGroup, onFilterChange]);

  const handleDeleteGroup = useCallback((groupId: string) => {
    // Deleting a group detaches (ungroups) its member agents backend-side —
    // the same simple, safe default the old "ungroup" delete option used
    // (it deleted the group directly, no per-agent update). No move/reassign
    // modal; group rename is dropped in this round.
    deleteGroup.mutate(groupId, {
      onSuccess: () => {
        if (groupScope === groupId) onFilterChange('group', '');
        toast.success('Group deleted');
      },
      onError: (err) => toast.error(err.message),
    });
  }, [deleteGroup, groupScope, onFilterChange]);

  const renderItem = (item: UnifiedPersona) => (
    <div
      data-testid={`agent-item-${item.id}`}
      className="rounded-lg px-3 py-2 transition-colors hover:bg-accent/50"
    >
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {personaInitial(item.name)}
      </div>
      <button
        type="button"
        className={cn('min-w-0 flex-1 text-left', item.source === 'system' && 'cursor-default')}
        onClick={() => { if (item.source === 'workspace') void navigate(`/agents/${item.id}`); }}
      >
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{item.name}</span>
          {item.source === 'system' && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              template
            </span>
          )}
          {item.group_name && <Badge variant="secondary">{item.group_name}</Badge>}
        </div>
        {item.description && (
          <p className="mt-0.5 truncate text-sm text-muted-foreground">{item.description}</p>
        )}
      </button>
      {item.source === 'system' ? (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`agent-copy-${item.id}`}
          disabled={copySystem.isPending}
          onClick={() => {
            copySystem.mutate(
              { systemPersonaId: item.id },
              {
                onSuccess: (result) => { void navigate(`/agents/${result.persona_id}`); },
                onError: (err) => toast.error(err.message),
              },
            );
          }}
        >
          Copy to My Agents
        </Button>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            data-testid={`agent-duplicate-${item.id}`}
            disabled={duplicateAgent.isPending}
            onClick={() => {
              if (!item.agent_config) return;
              duplicateAgent.mutate(
                {
                  name: `Copy of ${item.name}`,
                  agentConfig: item.agent_config,
                  description: item.description ?? undefined,
                  groupName: item.group_name ?? undefined,
                },
                {
                  onSuccess: (config) => { void navigate(`/agents/${config.persona_id}`); },
                  onError: (err) => toast.error(err.message),
                },
              );
            }}
          >
            Duplicate
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid={`agent-delete-${item.id}`}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={deleteAgent.isPending}
            onClick={() => setPendingDeleteId(item.id)}
          >
            Delete
          </Button>
        </>
      )}
    </div>
    {pendingDeleteId === item.id && (
      <div className="mt-3 flex items-center justify-between rounded bg-destructive/10 px-3 py-2">
        <span className="text-sm text-destructive">
          {`Delete "${item.name}"? This cannot be undone.`}
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setPendingDeleteId(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            data-testid={`agent-confirm-delete-${item.id}`}
            disabled={deleteAgent.isPending}
            onClick={() => {
              deleteAgent.mutate(item.id, {
                onSuccess: () => setPendingDeleteId(null),
                onError: (err) => toast.error(err.message),
              });
            }}
          >
            {deleteAgent.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    )}
    </div>
  );

  return (
    <div data-testid="agent-library-view">
      <DataList<UnifiedPersona>
        headerLeading={
          <div className="flex flex-wrap items-center gap-2" data-testid="agent-source-filter">
            {SOURCE_CHIPS.map((chip) => {
              const active = sourceScope === chip.value;
              return (
                <button
                  key={chip.value || 'all'}
                  type="button"
                  data-testid={chip.testid}
                  aria-pressed={active}
                  onClick={() => setSourceScope(chip.value)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  {chip.label}
                </button>
              );
            })}
            <div className="ml-1">
              <GroupFilterDropdown
                groups={groupOptions}
                selectedGroupId={groupScope}
                onSelect={(id) => onFilterChange('group', id)}
                onCreate={handleCreateGroup}
                onDelete={handleDeleteGroup}
                isBusy={createGroup.isPending || deleteGroup.isPending}
              />
            </div>
          </div>
        }
        items={pagedItems}
        renderItem={renderItem}
        getItemId={(p) => p.id}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search agents..."
        page={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={pagination.onPageChange}
        totalItems={filteredItems.length}
        pageSize={pagination.pageSize}
        pageSizeOptions={pagination.pageSizeOptions}
        onPageSizeChange={pagination.onPageSizeChange}
        isLoading={workspaceLoading || systemLoading}
        headerActions={
          <Button data-testid="agent-new-button" onClick={() => { void navigate('/agents/new'); }}>
            + New Agent
          </Button>
        }
        emptyState={
          <div className="text-center" data-testid="agent-library-empty">
            {workspaceError ? (
              <p className="text-sm text-muted-foreground">
                Failed to load agents. Make sure the backend service is running.
              </p>
            ) : sourceScope === 'mine' && !searchValue && !groupScope ? (
              <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                You have no agents yet —
                <button
                  type="button"
                  data-testid="agent-empty-browse-templates"
                  onClick={() => setSourceScope('templates')}
                  className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground hover:bg-secondary/80"
                >
                  browse Templates
                </button>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {sourceScope === 'templates' && !searchValue
                  ? 'No templates found.'
                  : 'No agents match your filters.'}
              </p>
            )}
          </div>
        }
      />
    </div>
  );
}
